import React, { useEffect, useState } from 'react';
import { Sparkles, Zap, Target, TrendingUp, Cpu, Server } from 'lucide-react';
import { generateAppImage } from '../services/imageService';

interface ShowcasePost {
  id: string;
  title: string;
  description: string;
  prompt: string;
  icon: any;
  image?: string;
}

const INITIAL_POSTS: ShowcasePost[] = [
  {
    id: '1',
    title: 'Otimização de Copy em Tempo Real',
    description: 'Nossa IA analisa o comportamento do usuário e ajusta a copy da sua landing page instantaneamente para maximizar a retention.',
    prompt: 'Futuristic AI brain analyzing digital text streams, neon blue highlights, high-tech interface',
    icon: Zap
  },
  {
    id: '2',
    title: 'Previsão de Conversão Preditiva',
    description: 'Algoritmos avançados que preveem a probabilidade de conversão de cada visitante antes mesmo deles clicarem no botão.',
    prompt: 'Digital crystal ball showing rising conversion charts, emerald green glow, data visualization',
    icon: Target
  },
  {
    id: '3',
    title: 'Infraestrutura de Escala Global',
    description: 'Sistema auto-escalável que suporta picos massivos de tráfego sem perder 1ms de performance.',
    prompt: 'Global network of servers in space, futuristic satellite connections, deep indigo space aesthetic',
    icon: Server
  }
];

export const AIShowcase: React.FC = () => {
  const [posts, setPosts] = useState<ShowcasePost[]>(INITIAL_POSTS);
  const [loading, setLoading] = useState(true);

  const loadImages = async () => {
    setLoading(true);
    try {
      const updatedPosts = await Promise.all(
        INITIAL_POSTS.map(async (post) => {
          const imageUrl = await generateAppImage(post.prompt);
          return { ...post, image: imageUrl || `https://picsum.photos/seed/${post.id}/800/450` };
        })
      );
      setPosts(updatedPosts);
    } catch (err: any) {
      console.error("Error loading showcase images:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadImages();
  }, []);

  return (
    <div className="space-y-8">
      <div className="flex flex-col space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-indigo-500/20 flex items-center justify-center border border-indigo-500/30">
            <Sparkles className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-white tracking-tight">Galeria de Inovação IA</h2>
            <p className="text-xs text-slate-500 font-bold uppercase tracking-[0.2em]">Artes Geradas sob Demanda pelo Motor OptiVelo</p>
          </div>
        </div>
        <p className="text-sm text-slate-400 max-w-2xl leading-relaxed italic">
          "Para elevar o nível do projeto, implementamos um serviço de inteligência artificial exclusivo que gera estas artes sob demanda. O sistema cria imagens originais e futuristas que representam exatamente o poder da nossa plataforma."
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {posts.map((post) => (
          <div key={post.id} className="premium-card group overflow-hidden flex flex-col h-full">
            <div className="relative aspect-video overflow-hidden">
              {loading ? (
                <div className="w-full h-full bg-white/5 animate-pulse flex items-center justify-center">
                  <Cpu className="w-8 h-8 text-indigo-500/30 animate-spin" />
                </div>
              ) : (
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  referrerPolicy="no-referrer"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-60" />
              <div className="absolute top-4 left-4 p-2 bg-black/40 backdrop-blur-md rounded-xl border border-white/10">
                <post.icon className="w-4 h-4 text-indigo-400" />
              </div>
            </div>
            
            <div className="p-6 space-y-3 flex-grow flex flex-col">
              <h3 className="text-lg font-bold text-white tracking-tight group-hover:text-indigo-400 transition-colors">
                {post.title}
              </h3>
              <p className="text-xs text-slate-400 leading-relaxed flex-grow">
                {post.description}
              </p>
              <div className="pt-4 flex items-center justify-between border-t border-white/5">
                <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-widest">Status: Ativo</span>
                <TrendingUp className="w-3 h-3 text-emerald-500" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
